import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

// Carry an already-issued session into a freshly opened same-origin tab: the
// opener appends #t=<token> to the URL. Capture it once, keep it for this tab,
// then strip the hash so it isn't left in the address bar. (Token never goes in
// the query string and is not sent to the server.)
function captureHandoffToken() {
  const h = window.location.hash || "";
  const m = h.match(/[#&]t=([^&]+)/);
  if (!m) return sessionStorage.getItem("revq.token");
  const t = decodeURIComponent(m[1]);
  try { sessionStorage.setItem("revq.token", t); } catch (e) { /* ignore */ }
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return t;
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => captureHandoffToken());
  const [user, setUser] = useState(() => {
    const raw = sessionStorage.getItem("revq.user");
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [bootstrapping, setBootstrapping] = useState(!!token && !user);

  // If we have a token but no cached profile, fetch it.
  useEffect(() => {
    if (!token || user) { setBootstrapping(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/ehibridge/auth/profile");
        if (!cancelled) {
          setUser(data);
          sessionStorage.setItem("revq.user", JSON.stringify(data));
        }
      } catch {
        if (!cancelled) signOut();
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = useCallback((accessToken, userObj) => {
    sessionStorage.setItem("revq.token", accessToken);
    sessionStorage.removeItem("revq.client");
    // Open chart tabs are per-tenant — never carry them into a new session.
    sessionStorage.removeItem("revq.chartTabs");
    sessionStorage.removeItem("revq.chartTabsV2");
    sessionStorage.removeItem("revq.cc.state");
    sessionStorage.removeItem("revq.er.state");
    if (userObj) sessionStorage.setItem("revq.user", JSON.stringify(userObj));
    setToken(accessToken);
    if (userObj) setUser(userObj);
  }, []);

  const signOut = useCallback(() => {
    sessionStorage.removeItem("revq.token");
    sessionStorage.removeItem("revq.user");
    sessionStorage.removeItem("revq.client");
    sessionStorage.removeItem("revq.chartTabs");
    sessionStorage.removeItem("revq.chartTabsV2");
    sessionStorage.removeItem("revq.cc.state");
    sessionStorage.removeItem("revq.er.state");
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ token, user, bootstrapping, signIn, signOut }),
    [token, user, bootstrapping, signIn, signOut],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth() outside AuthProvider");
  return ctx;
}
